import React, { useCallback, useEffect, useMemo, useState } from "react";
import ApproveOrders from "./ApproveOrders";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import api, {
  ApiResponse,
  getPlatformLogo,
  getDeliveryQuote,
  updateOrderDeliveryExternal,
  updateOrderDelivery,
} from "../services/api";
import { useOrder } from "./EmpOrderContext";
import { Order } from "../utils";
import Modal from "react-bootstrap/Modal";
import DoorDash from "../../src/assests/DASH.svg";
import Uber from "../../src/assests/uber-icon.svg";
type OrdersTab = "all" | "scheduled" | "delivery" | "completed" | "approve";
type ScheduledPlatformFilter = "all" | "uber" | "doordash" | "pickup";
type CompletedOrderFilter = "all" | "delivery" | "pickup" | "dinein";

interface HotelConfig {
  latitude: number;
  longitude: number;
  withinRadius: number;
  minOrderAmount: number;
}

interface DeliveryQuoteResponse {
  success?: boolean;
  quotes?: {
    uber?: {
      success?: boolean;
      data?: {
        fee?: number;
        currency?: string;
        currency_type?: string;
        id?: string;
        delivery_id?: string;
        deliveryId?: string;
        delivery?: { id?: string };
        quote_id?: string;
        quoteId?: string;
      };
      error?: any;
    };
    doordash?: {
      success?: boolean;
      data?: {
        fee?: number;
        currency?: string;
        currency_type?: string;
        id?: string;
        delivery_id?: string;
        deliveryId?: string;
        delivery?: { id?: string };
        quote_id?: string;
        quoteId?: string;
      };
      error?: any;
    };
    deliveryTax?: number;
  };
}

const EmpAllOrders: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useOrder();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState("");
  const [activeTab, setActiveTab] = useState<OrdersTab>("all");
  const [scheduledPlatformFilter, setScheduledPlatformFilter] =
    useState<ScheduledPlatformFilter>("all");
  const [completedOrderFilter, setCompletedOrderFilter] =
    useState<CompletedOrderFilter>("all");
  const [lastOrderTimestamp, setLastOrderTimestamp] = useState<string | null>(
    null,
  );
  const [hotelConfig, setHotelConfig] = useState<HotelConfig | null>(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryModalOrder, setDeliveryModalOrder] = useState<Order | null>(
    null,
  );
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoteData, setQuoteData] = useState<DeliveryQuoteResponse | null>(
    null,
  );
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Order[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const excludedStatuses = useMemo(
    () => new Set(["cancelled", "preparing", "delivered"]),
    [],
  );

  const sortByCreatedAtDesc = useCallback((items: Order[]) => {
    return [...items].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, []);

  const filterIncomingStatuses = useCallback(
    (items: Order[]) =>
      items.filter(
        (order) =>
          !excludedStatuses.has((order.orderStatus || "").toLowerCase()),
      ),
    [excludedStatuses],
  );

  const searchOrders = useCallback(
    async (searchTerm: string) => {
      if (!user?.restaurantId) return;

      const trimmed = searchTerm.trim();
      if (!trimmed) {
        setSearchResults([]);
        return;
      }

      try {
        setSearchLoading(true);
        const token = Cookies.get("emptoken");
        const params = new URLSearchParams();
        params.set("restaurantId", String(user.restaurantId));
        params.set("query", trimmed);
        params.set("limit", "50");

        const res = await api.get<ApiResponse<Order[]>>(
          `/orders/search?${params.toString()}`,
          token
            ? { headers: { Authorization: `Bearer ${token}` } }
            : undefined,
        );

        if (!res.data?.success || !Array.isArray(res.data.data)) {
          setSearchResults([]);
          return;
        }

        setSearchResults(sortByCreatedAtDesc(res.data.data));
      } catch (error) {
        console.error("Failed to search orders", error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [sortByCreatedAtDesc, user?.restaurantId],
  );

  const buildOrdersUrl = useCallback(
    (options?: { after?: string; limit?: number }) => {
      if (!user?.restaurantId) return "";
      if (activeTab === "approve") return "";
      const params = new URLSearchParams();
      params.set("restaurantId", String(user.restaurantId));
      if (activeTab === "all") {
        params.set("status", "incoming");
      }
      if (activeTab === "scheduled") {
        params.set("scheduled", "true");
      }
      if (activeTab === "delivery") {
        params.set("deliveryPending", "true");
      }
      if (activeTab === "completed") {
        params.set("status", "completed");
      }
      if (options?.after) {
        params.set("after", options.after);
      }
      if (typeof options?.limit === "number") {
        params.set("limit", String(options.limit));
      }
      return `/orders?${params.toString()}`;
    },
    [activeTab, user?.restaurantId],
  );

  const fetchOrders = useCallback(async () => {
    if (!user?.restaurantId || activeTab === "approve") return;
    try {
      setLoading(true);
      const token = Cookies.get("emptoken");
      const url = buildOrdersUrl({ limit: 30 });
      const res = await api.get<ApiResponse<Order[]>>(
        url,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );

      if (!res.data?.success || !Array.isArray(res.data.data)) {
        setOrders([]);
        setLastOrderTimestamp(null);
        return;
      }

      const filtered = filterIncomingStatuses(res.data.data);
      const sorted = sortByCreatedAtDesc(filtered).slice(0, 30);
      setOrders(sorted);
      setLastOrderTimestamp(sorted[0]?.createdAt || null);
    } catch (error) {
      console.error("Failed to fetch orders", error);
      setOrders([]);
      setLastOrderTimestamp(null);
    } finally {
      setLoading(false);
    }
  }, [
    buildOrdersUrl,
    filterIncomingStatuses,
    sortByCreatedAtDesc,
    user?.restaurantId,
  ]);

  const fetchNewOrders = useCallback(async () => {
    if (!user?.restaurantId || !lastOrderTimestamp) return;
    try {
      const token = Cookies.get("emptoken");
      const url = buildOrdersUrl({ after: lastOrderTimestamp });
      const res = await api.get<ApiResponse<Order[]>>(
        url,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );

      if (!res.data?.success || !Array.isArray(res.data.data)) {
        return;
      }

      const incoming = filterIncomingStatuses(res.data.data);
      if (incoming.length === 0) return;

      const sortedIncoming = sortByCreatedAtDesc(incoming);
      setOrders((prev) => {
        const merged = [...sortedIncoming, ...prev];
        const byId = new Map<string, Order>();
        for (const order of merged) {
          byId.set(order._id, order);
        }
        const deduped = Array.from(byId.values());
        return sortByCreatedAtDesc(deduped).slice(0, 30);
      });
      setLastOrderTimestamp(sortedIncoming[0]?.createdAt || lastOrderTimestamp);
    } catch (error) {
      console.error("Failed to fetch new orders", error);
    }
  }, [
    buildOrdersUrl,
    filterIncomingStatuses,
    lastOrderTimestamp,
    sortByCreatedAtDesc,
    user?.restaurantId,
  ]);

  useEffect(() => {
    const getLogo = async () => {
      try {
        const logo = await getPlatformLogo();
        if (logo.data.success && logo.data.data?.logo) {
          setLogoUrl(logo.data.data.logo);
        } else {
          setLogoUrl("");
        }
      } catch {
        setLogoUrl("");
      }
    };

    getLogo();
  }, []);

  useEffect(() => {
    if (!user?.restaurantId) return;
    fetchOrders();
  }, [fetchOrders, user?.restaurantId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchInput.trim());
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    if (!debouncedSearchTerm) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    searchOrders(debouncedSearchTerm);
  }, [debouncedSearchTerm, searchOrders]);

  useEffect(() => {
    if (!user?.restaurantId) return;
    const interval = setInterval(fetchNewOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchNewOrders, user?.restaurantId]);

  useEffect(() => {
    if (!user?.restaurantId) return;
    const fetchHotelConfig = async () => {
      try {
        const res = await api.get<ApiResponse<any[]>>(
          "/admin/getHotelInformation",
        );
        if (!res.data?.success || !Array.isArray(res.data.data)) {
          setHotelConfig(null);
          return;
        }
        const hotel = res.data.data.find(
          (item) => String(item._id) === String(user.restaurantId),
        );
        if (!hotel) {
          setHotelConfig(null);
          return;
        }
        setHotelConfig({
          latitude: Number(hotel.latitude),
          longitude: Number(hotel.longitude),
          withinRadius: Number(hotel.withinRadius || 0),
          minOrderAmount: Number(hotel.minOrderAmount || 0),
        });
      } catch {
        setHotelConfig(null);
      }
    };

    fetchHotelConfig();
  }, [user?.restaurantId]);

  const showLogo = Boolean(logoUrl);

  const getOrderTotal = useCallback((order: Order) => {
    if (typeof order.totalAmount === "number") {
      return order.totalAmount;
    }
    return order.items.reduce(
      (sum, item) => sum + (item.price || 0) * item.quantity,
      0,
    );
  }, []);

  const getDistanceMiles = useCallback(
    (lat1?: number, lng1?: number, lat2?: number, lng2?: number) => {
      if (
        typeof lat1 !== "number" ||
        typeof lng1 !== "number" ||
        typeof lat2 !== "number" ||
        typeof lng2 !== "number"
      ) {
        return Number.POSITIVE_INFINITY;
      }

      const toRad = (value: number) => (value * Math.PI) / 180;
      const R = 3958.8;
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) *
          Math.cos(toRad(lat2)) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    },
    [],
  );

  const isDeliveryPending = useCallback(
    (order: Order) => {
      if (!hotelConfig) return false;
      const isOnline = (order.choice || "").toLowerCase() === "online";
      if (!isOnline) return false;

      const platform =
        typeof order.metadata?.platform === "string"
          ? order.metadata.platform.toLowerCase()
          : "custom";
      const deliveryStatus =
        typeof order.metadata?.deliveryStatus === "string"
          ? order.metadata.deliveryStatus.toLowerCase()
          : typeof (order as any).deliveryStatus === "string"
            ? String((order as any).deliveryStatus).toLowerCase()
            : "pending";
      const isCustomPlatform = platform === "custom";
      const isPending = deliveryStatus === "pending";
      if (!isCustomPlatform || !isPending) return false;

      const distance = getDistanceMiles(
        hotelConfig.latitude,
        hotelConfig.longitude,
        order.location?.lat,
        order.location?.lng,
      );
      const withinRadius = distance <= hotelConfig.withinRadius;
      const meetsMinAmount = getOrderTotal(order) >= hotelConfig.minOrderAmount;
      return withinRadius && meetsMinAmount;
    },
    [getDistanceMiles, getOrderTotal, hotelConfig],
  );

  const displayedOrders = useMemo(() => {
    if (debouncedSearchTerm) {
      if (searchResults.length > 0) {
        return searchResults;
      }

      const term = debouncedSearchTerm.trim().toLowerCase().replace(/^#/, "");
      const digits = term.replace(/\D/g, "");
      if (!term) return [];

      return sortByCreatedAtDesc(
        filterIncomingStatuses(orders).filter((order) => {
          const orderNumber = String(order.orderNumber || "")
            .toLowerCase()
            .replace(/^#/, "");
          const phoneRaw = String(order.phoneNumber || "").toLowerCase();
          const phoneDigits = phoneRaw.replace(/\D/g, "");

          return (
            orderNumber.includes(term) ||
            phoneRaw.includes(term) ||
            (digits.length > 0 && phoneDigits.includes(digits))
          );
        }),
      );
    }
    let filtered = filterIncomingStatuses(orders);

    // Apply platform/pickup filter when on scheduled tab
    if (activeTab === "scheduled" && scheduledPlatformFilter !== "all") {
      filtered = filtered.filter((order) => {
        if (scheduledPlatformFilter === "pickup") {
          return (order.choice || "").toLowerCase() === "takeaway";
        }
        const platform = (order.metadata?.platform || "").toLowerCase();
        return platform === scheduledPlatformFilter;
      });
    }

    // Apply delivery/pickup/dine-in filter when on completed tab
    if (activeTab === "completed" && completedOrderFilter !== "all") {
      filtered = filtered.filter((order) => {
        const choice = (order.choice || "").toLowerCase();
        if (completedOrderFilter === "delivery") return choice === "online";
        if (completedOrderFilter === "pickup") return choice === "takeaway";
        return choice === "dining in" || Boolean(order.tableToken);
      });
    }

    return filtered;
  }, [
    debouncedSearchTerm,
    filterIncomingStatuses,
    orders,
    searchResults,
    activeTab,
    scheduledPlatformFilter,
    completedOrderFilter,
    sortByCreatedAtDesc,
  ]);

  const setOrderPlatform = useCallback((orderId: string, platform: string) => {
    const providerNormalized = platform.toLowerCase();
    setOrders((prev) =>
      prev.map((order) =>
        order._id === orderId
          ? {
              ...order,
              metadata: {
                ...(order.metadata || {}),
                platform: providerNormalized,
              },
            }
          : order,
      ),
    );
  }, []);

  const assignDeliveryProvider = useCallback(
    async (
      orderId: string,
      provider: "DoorDash" | "Uber" | "Custom Delivery",
    ) => {
      try {
        const res = await updateOrderDelivery(orderId, provider);
        if (res.data?.success) {
          setOrderPlatform(orderId, provider);
        }
      } catch (error) {
        console.error("Failed to assign delivery provider", error);
      }
    },
    [setOrderPlatform],
  );

  const closeDeliveryModal = useCallback(() => {
    setShowDeliveryModal(false);
    setDeliveryModalOrder(null);
    setQuoteLoading(false);
    setQuoteError(null);
    setQuoteData(null);
  }, []);

  const openDeliveryModal = useCallback(async (order: Order) => {
    setDeliveryModalOrder(order);
    setShowDeliveryModal(true);
    setQuoteLoading(true);
    setQuoteError(null);
    setQuoteData(null);

    const dropoff_address = order.metadata?.dropoff_address;
    const dropoff_latitude = Number(order.metadata?.dropoff_latitude);
    const dropoff_longitude = Number(order.metadata?.dropoff_longitude);
    const pickup_time =
      typeof order.metadata?.pickup_time === "string" &&
      order.metadata.pickup_time.trim().length > 0
        ? order.metadata.pickup_time
        : new Date().toISOString();

    if (
      !dropoff_address ||
      Number.isNaN(dropoff_latitude) ||
      Number.isNaN(dropoff_longitude)
    ) {
      setQuoteError("Missing dropoff details for quote.");
      setQuoteLoading(false);
      return;
    }

    try {
      const res = await getDeliveryQuote({
        dropoff_address,
        dropoff_latitude,
        dropoff_longitude,
        pickup_time,
      });
      setQuoteData(res.data);
    } catch (error) {
      console.error("Failed to fetch delivery quote", error);
      setQuoteError("Failed to fetch delivery quote.");
    } finally {
      setQuoteLoading(false);
    }
  }, []);

  const formatQuoteFee = useCallback((fee?: number, currency?: string) => {
    if (typeof fee !== "number") return "N/A";
    const amount = (fee / 100).toFixed(2);
    const code = (currency || "").toUpperCase();
    return code ? `${code} ${amount}` : `$${amount}`;
  }, []);

  const uberQuote = quoteData?.quotes?.uber;
  const doordashQuote = quoteData?.quotes?.doordash;

  const getQuoteDeliveryId = useCallback(
    (provider: "uber" | "doordash") => {
      const data =
        provider === "uber"
          ? quoteData?.quotes?.uber?.data
          : quoteData?.quotes?.doordash?.data;
      if (!data) return undefined;
      return (
        data.deliveryId ||
        data.delivery_id ||
        data.id ||
        data.delivery?.id ||
        data.quoteId ||
        data.quote_id
      );
    },
    [quoteData],
  );

  const handleTabChange = (tab: OrdersTab) => {
    if (tab !== activeTab) {
      setSearchInput("");
      setDebouncedSearchTerm("");
      setSearchResults([]);
      setScheduledPlatformFilter("all");
      setCompletedOrderFilter("all");
    }
    setActiveTab(tab);
  };

  const topBar = (
    <div className="border-b border-gray-200">
      <div className="h-[100px] px-4 md:px-6 flex items-center justify-between">
        <div className="w-[100px] flex items-center">
          {showLogo ? (
            <img
              src={logoUrl}
              alt="Platform logo"
              className="w-auto h-[85px] object-contain"
            />
          ) : (
            <div className="text-[26px] font-extrabold leading-none text-[#222]">
              Qleap
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-4 md:gap-6 lg:gap-8">
          <button
            onClick={() => navigate("/Sales")}
            className="px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-gray-500 hover:text-gray-800"
          >
            POS
          </button>
          <button className="px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold bg-blue-600 text-white shadow-sm">
            All Orders
          </button>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by order # or phone"
            maxLength={10}
            className="w-[180px] sm:w-[240px] md:w-[300px] px-3 py-2 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="w-[100px]" />
      </div>
    </div>
  );

  const renderOrderCard = useCallback(
    (order: Order) => {
      const total = getOrderTotal(order);
      const showDeliveryAssignment = isDeliveryPending(order);
      const orderTime = new Date(order.createdAt);

      return (
        <div
          key={order._id}
          className="w-80 mt-3 border border-gray-200 rounded-xl overflow-hidden font-sans shadow-sm bg-white flex flex-col"
        >
          <div
            className="p-2 text-white font-semibold text-sm"
            style={{ backgroundColor: "#113E60" }}
          >
            <div className="flex justify-between">
              <span className="font-outfit">Order</span>
              <span className="text-right font-outfit">
                #{order.orderNumber}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-s font-outfit text-white/80 capitalize">
                {order.choice || "counter"}
              </span>
              <span className="text-right font-outfit text-white/80">
                {orderTime.toLocaleTimeString()}
              </span>
            </div>
            {activeTab === "completed" && (
              <div className="flex justify-between">
                <span className="text-s font-outfit text-white/80">
                  Order Type
                </span>
                <span className="text-right font-outfit text-white/80">
                  {order.tableToken
                    ? `Table #${order.tableToken}`
                    : (order.choice || "").toLowerCase() === "online"
                      ? "Delivery"
                      : "Pickup"}
                </span>
              </div>
            )}
            {order?.metadata?.platform && (
              <div className="flex justify-between">
                <span className="text-s font-outfit text-white/80 capitalize">
                  Delivery partner
                </span>
                <span className="text-right font-outfit text-white/80">
                  {order.metadata.platform.charAt(0).toUpperCase() +
                    order.metadata.platform.slice(1)}
                </span>
              </div>
            )}
            {order.scheduledAt && (
              <div className="font-outfit text-white/90">
                Scheduled for: {new Date(order.scheduledAt).toLocaleString()}
              </div>
            )}
          </div>

          <div className="px-3 pt-3 text-xs text-gray-400 flex justify-between">
            <span>Items</span>
            <span>Qty</span>
            <span>Price</span>
          </div>

          <div className="overflow-y-auto flex-1 max-h-52 no-scrollbar">
            {order.items.map((item, index) => (
              <div
                key={`${order._id}-${item.itemId}-${index}`}
                className="p-3 border-b border-gray-200 text-sm flex justify-between items-center gap-2"
              >
                <span className="font-outfit flex-1 text-[#113E60]">
                  {item.kitchenName || item.name}
                </span>
                <span className="font-outfit text-[#113E60]">
                  {item.quantity}
                </span>
                <span className="font-outfit text-[#113E60]">
                  ${((item.price || 0) * item.quantity).toFixed(2)}
                </span>
              </div>
            ))}
          </div>

          <div className="px-3 py-2 text-xs text-[#113E60] font-outfit bg-white w-full flex justify-between">
            <span>{orderTime.toLocaleDateString()}</span>
            <span>{orderTime.toLocaleTimeString()}</span>
          </div>

          <div className="px-3 py-2 flex justify-between text-sm font-semibold text-[#113E60]">
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>

          {showDeliveryAssignment && (
            <div className="px-3 pb-3 flex flex-wrap gap-2">
              <span className="text-xs font-outfit text-[#113E60] mt-[5px]">
                Delivery Pending
              </span>
              <button
                onClick={() => openDeliveryModal(order)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-600 text-white shadow-sm hover:bg-blue-700"
              >
                Edit Delivery
              </button>
            </div>
          )}
        </div>
      );
    },
    [getOrderTotal, isDeliveryPending, openDeliveryModal, activeTab],
  );

  const orderedCards = useMemo(() => {
    return displayedOrders.map((order) => renderOrderCard(order));
  }, [displayedOrders, renderOrderCard]);

  return (
    <div className="min-h-screen">
      {topBar}
      <div className="px-6 py-3">
        <div className="flex flex-wrap gap-3 sm:gap-5">
          <button
            onClick={() => handleTabChange("all")}
            className={
              activeTab === "all"
                ? "px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 text-white shadow-sm"
                : "px-4 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:text-gray-800"
            }
          >
            All Orders
          </button>
          <button
            onClick={() => handleTabChange("scheduled")}
            className={
              activeTab === "scheduled"
                ? "px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 text-white shadow-sm"
                : "px-4 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:text-gray-800"
            }
          >
            Scheduled Orders
          </button>
          <button
            onClick={() => handleTabChange("delivery")}
            className={
              activeTab === "delivery"
                ? "px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 text-white shadow-sm"
                : "px-4 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:text-gray-800"
            }
          >
            Delivery Pending
          </button>
          <button
            onClick={() => handleTabChange("completed")}
            className={
              activeTab === "completed"
                ? "px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 text-white shadow-sm"
                : "px-4 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:text-gray-800"
            }
          >
            Completed Orders
          </button>
          {/* Approve Orders tab - disabled for now, keep for future use
          <button
            onClick={() => setActiveTab("approve")}
            className={
              activeTab === "approve"
                ? "px-4 py-2 rounded-xl text-sm font-semibold bg-orange-500 text-white shadow-sm"
                : "px-4 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:text-gray-800"
            }
          >
            Approve Orders
          </button>
          */}
        </div>

        {/* Sub-tabs for Scheduled Orders - filter by platform */}
        {activeTab === "scheduled" && (
          <div className="flex flex-wrap gap-2 mt-3 ml-1">
            <button
              onClick={() => setScheduledPlatformFilter("all")}
              className={
                scheduledPlatformFilter === "all"
                  ? "px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-800 text-white"
                  : "px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200"
              }
            >
              All
            </button>
            <button
              onClick={() => setScheduledPlatformFilter("uber")}
              className={
                scheduledPlatformFilter === "uber"
                  ? "px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-800 text-white flex items-center gap-1.5"
                  : "px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 flex items-center gap-1.5"
              }
            >
              <img src={Uber} alt="Uber" className="w-4 h-4" />
              Uber
            </button>
            <button
              onClick={() => setScheduledPlatformFilter("doordash")}
              className={
                scheduledPlatformFilter === "doordash"
                  ? "px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-800 text-white flex items-center gap-1.5"
                  : "px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 flex items-center gap-1.5"
              }
            >
              <img src={DoorDash} alt="DoorDash" className="w-4 h-4" />
              DoorDash
            </button>
            <button
              onClick={() => setScheduledPlatformFilter("pickup")}
              className={
                scheduledPlatformFilter === "pickup"
                  ? "px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-800 text-white"
                  : "px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200"
              }
            >
              Pickup
            </button>
          </div>
        )}

        {/* Sub-tabs for Completed Orders - filter by delivery vs pickup vs dine-in */}
        {activeTab === "completed" && (
          <div className="flex flex-wrap gap-2 mt-3 ml-1">
            <button
              onClick={() => setCompletedOrderFilter("all")}
              className={
                completedOrderFilter === "all"
                  ? "px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-800 text-white"
                  : "px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200"
              }
            >
              All
            </button>
            <button
              onClick={() => setCompletedOrderFilter("delivery")}
              className={
                completedOrderFilter === "delivery"
                  ? "px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-800 text-white"
                  : "px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200"
              }
            >
              Delivery
            </button>
            <button
              onClick={() => setCompletedOrderFilter("pickup")}
              className={
                completedOrderFilter === "pickup"
                  ? "px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-800 text-white"
                  : "px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200"
              }
            >
              Pickup
            </button>
            <button
              onClick={() => setCompletedOrderFilter("dinein")}
              className={
                completedOrderFilter === "dinein"
                  ? "px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-800 text-white"
                  : "px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200"
              }
            >
              Dine-in
            </button>
          </div>
        )}
      </div>

      {activeTab === "approve" ? (
        <ApproveOrders />
      ) : (
      <div className="px-5 pb-8">
        {loading || searchLoading ? (
          <p className="text-gray-500">Loading orders...</p>
        ) : displayedOrders.length === 0 ? (
          <p className="text-gray-500">
            {debouncedSearchTerm
              ? "No matching orders found."
              : "No orders available."}
          </p>
        ) : (
          <div className="flex flex-wrap gap-4 justify-start mt-2">
            {orderedCards}
          </div>
        )}
      </div>
      )}

      <Modal show={showDeliveryModal} onHide={closeDeliveryModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delivery Providers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {quoteLoading ? (
            <p className="text-gray-500">Loading delivery quotes...</p>
          ) : quoteError ? (
            <p className="text-red-500 text-sm">{quoteError}</p>
          ) : (
            <div className="flex flex-col gap-3">
              <button
                onClick={async () => {
                  if (!deliveryModalOrder) return;
                  const deliveryId = getQuoteDeliveryId("doordash");
                  if (!deliveryId) {
                    setQuoteError("Missing DoorDash delivery ID from quote.");
                    return;
                  }
                  try {
                    await updateOrderDeliveryExternal({
                      orderId: deliveryModalOrder._id,
                      platform: "DoorDash",
                      deliveryId,
                      tipAmount: (deliveryModalOrder as any).tipAmount,
                      metadata: deliveryModalOrder.metadata,
                      items: deliveryModalOrder.items,
                      totalAmount: getOrderTotal(deliveryModalOrder),
                      userId: (deliveryModalOrder as any).userId,
                      orderBy: (deliveryModalOrder as any).orderBy,
                    });
                    setOrderPlatform(deliveryModalOrder._id, "DoorDash");
                    closeDeliveryModal();
                  } catch (error) {
                    console.error("Failed to update DoorDash delivery", error);
                  }
                }}
                disabled={!doordashQuote?.success}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-full text-xs font-semibold border border-gray-200 text-gray-700 disabled:opacity-40"
              >
                <span className="flex items-center gap-2">
                  <img src={DoorDash} alt="DoorDash" className="w-5 h-5" />
                  DoorDash
                </span>
                <span className="text-[11px] text-gray-500">
                  {doordashQuote?.success
                    ? formatQuoteFee(
                        doordashQuote.data?.fee,
                        doordashQuote.data?.currency ||
                          doordashQuote.data?.currency_type,
                      )
                    : "Unavailable"}
                </span>
              </button>
              <button
                onClick={async () => {
                  if (!deliveryModalOrder) return;
                  const deliveryId = getQuoteDeliveryId("uber");
                  if (!deliveryId) {
                    setQuoteError("Missing Uber delivery ID from quote.");
                    return;
                  }
                  try {
                    await updateOrderDeliveryExternal({
                      orderId: deliveryModalOrder._id,
                      platform: "Uber",
                      deliveryId,
                      tipAmount: (deliveryModalOrder as any).tipAmount,
                      metadata: deliveryModalOrder.metadata,
                      items: deliveryModalOrder.items,
                      totalAmount: getOrderTotal(deliveryModalOrder),
                      userId: (deliveryModalOrder as any).userId,
                      orderBy: (deliveryModalOrder as any).orderBy,
                    });
                    setOrderPlatform(deliveryModalOrder._id, "Uber");
                    closeDeliveryModal();
                  } catch (error) {
                    console.error("Failed to update Uber delivery", error);
                  }
                }}
                disabled={!uberQuote?.success}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-full text-xs font-semibold border border-gray-200 text-gray-700 disabled:opacity-40"
              >
                <span className="flex items-center gap-2">
                  <img src={Uber} alt="Uber" className="w-5 h-5" />
                  Uber
                </span>
                <span className="text-[11px] text-gray-500">
                  {uberQuote?.success
                    ? formatQuoteFee(
                        uberQuote.data?.fee,
                        uberQuote.data?.currency ||
                          uberQuote.data?.currency_type,
                      )
                    : "Unavailable"}
                </span>
              </button>
              <button
                onClick={async () => {
                  if (!deliveryModalOrder) return;
                  await assignDeliveryProvider(
                    deliveryModalOrder._id,
                    "Custom Delivery",
                  );
                  closeDeliveryModal();
                }}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-full text-xs font-semibold border border-gray-200 text-gray-700"
              >
                <span>Custom Delivery</span>
              </button>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EmpAllOrders;
